/**
 * Admin Dashboard — live system overview at a glance.
 *
 * Pulls real data from existing API adapters:
 *   - User counts + online presence (Security API)
 *   - Project counts by status (Projects API)
 *   - Community entity counts (CommunityMetadata API)
 *   - API health summary (apiHealthService)
 *   - Waitlist counts (Waitlist API)
 *   - Recent audit events (Audit Log API)
 *
 * English only — system admin UI (per CLAUDE.md).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAllSecurityUsers, type SecurityUserResponse } from '../../api/security';
import { fetchAllProjects, type ProjectResponse } from '../../api/projects';
import { fetchWaitlistCounts, type WaitlistCounts } from '../../api/waitlist';
import { useApiHealth, type HealthSnapshot } from '../../api/apiHealthService';
import { apiFetch } from '../../api/client';
import styles from './AdminPages.module.css';

// ── Types ──────────────────────────────────────────────────────

interface AuditEntry {
  id: number;
  eventType: string;
  userGuid: string;
  actorGuid: string | null;
  ipAddress: string | null;
  details: string | null;
  createdAt: string;
}

interface AuditPage {
  page: number;
  pageSize: number;
  totalCount: number;
  entries: AuditEntry[];
}

interface CommunityEntity {
  entityGuid: string;
  entityType: string;
  name: string;
  isActive: boolean;
}

interface DashboardData {
  users: SecurityUserResponse[];
  projects: ProjectResponse[];
  communities: CommunityEntity[];
  waitlist: WaitlistCounts;
  auditLog: AuditPage | null;
}

// ── Helpers ────────────────────────────────────────────────────

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function healthColor(status: HealthSnapshot['overallStatus']): string {
  switch (status) {
    case 'healthy': return '#16a34a';
    case 'degraded': return '#d97706';
    case 'down': return '#dc2626';
    default: return '#6b7280';
  }
}

function healthLabel(status: HealthSnapshot['overallStatus']): string {
  switch (status) {
    case 'healthy': return 'All Systems Healthy';
    case 'degraded': return 'Degraded';
    case 'down': return 'Systems Down';
    case 'checking': return 'Checking\u2026';
    default: return 'Unknown';
  }
}

// ── Inline styles (admin-only, English-only surface) ───────────

const S = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: 16,
    marginBottom: 24,
  } as React.CSSProperties,
  statCard: {
    padding: '18px 20px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  } as React.CSSProperties,
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: '#1a1a2e',
    lineHeight: 1.2,
  } as React.CSSProperties,
  statLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#6b7280',
    marginTop: 4,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  } as React.CSSProperties,
  statSub: {
    fontSize: 12,
    color: '#9ca3af',
    marginTop: 6,
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 12px 0',
  } as React.CSSProperties,
  row: {
    display: 'flex',
    gap: 24,
    marginBottom: 24,
  } as React.CSSProperties,
  panel: {
    flex: 1,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: 0,
  } as React.CSSProperties,
  auditRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '8px 0',
    borderBottom: '1px solid #f3f4f6',
    fontSize: 13,
  } as React.CSSProperties,
  badge: (color: string) => ({
    display: 'inline-block',
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 4,
    background: `${color}14`,
    color,
  } as React.CSSProperties),
  dot: (color: string) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  } as React.CSSProperties),
  quickLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    background: 'none',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: '#374151',
    textAlign: 'start' as const,
    transition: 'all 0.15s ease',
    width: '100%',
  } as React.CSSProperties,
  loadingBox: {
    padding: 40,
    textAlign: 'center' as const,
    color: '#9ca3af',
    fontSize: 14,
  } as React.CSSProperties,
};

// ── Quick links for bottom section ─────────────────────────────

const QUICK_LINKS = [
  { to: '/admin/users',     label: 'Manage Users',     icon: '⊕' },
  { to: '/admin/access',    label: 'Access Control',   icon: '⊗' },
  { to: '/admin/landing',   label: 'Landing Page',     icon: '⊡' },
  { to: '/admin/platform',  label: 'Platform Settings', icon: '⊛' },
  { to: '/admin/api',       label: 'API Monitor',      icon: '⊙' },
  { to: '/admin/geo',       label: 'Geographies',      icon: '◉' },
];

// ── Component ──────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate();
  const health = useApiHealth();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [users, projects, commRaw, waitlist, auditRaw] = await Promise.allSettled([
        fetchAllSecurityUsers(),
        fetchAllProjects(),
        apiFetch<CommunityEntity[]>('/api/CommunityMetadata/entities/all?includeInactive=true'),
        fetchWaitlistCounts(),
        apiFetch<AuditPage>('/api/admin/audit-log?page=1&pageSize=10'),
      ]);

      setData({
        users: users.status === 'fulfilled' ? users.value : [],
        projects: projects.status === 'fulfilled' ? projects.value : [],
        communities: commRaw.status === 'fulfilled' && commRaw.value ? commRaw.value : [],
        waitlist: waitlist.status === 'fulfilled' ? waitlist.value : {},
        auditLog: auditRaw.status === 'fulfilled' ? auditRaw.value : null,
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Derived stats ──────────────────────────────────────────
  const userCount = data?.users.length ?? 0;
  const activeUsers = data?.users.filter((u) => u.isActive).length ?? 0;
  const adminCount = data?.users.filter((u) => u.isSystemAdmin).length ?? 0;
  const projectCount = data?.projects.length ?? 0;
  const activeProjects = data?.projects.filter((p) => p.status === 'active' || p.projectStatus === 'Active').length ?? 0;
  const communityCount = data?.communities.filter((c) => c.isActive).length ?? 0;
  const totalCommunities = data?.communities.length ?? 0;
  const waitlistPending = data?.waitlist.pending ?? data?.waitlist.Pending ?? 0;
  const waitlistTotal = Object.values(data?.waitlist ?? {}).reduce((a, b) => a + b, 0);

  const healthyEndpoints = health.endpoints.filter((e) => e.status === 'up').length;
  const totalEndpoints = health.endpoints.length;

  if (loading) {
    return (
      <div>
        <h1 className={styles.pageTitle}>Dashboard</h1>
        <p className={styles.pageSubtitle}>System administration overview</p>
        <div style={S.loadingBox}>Loading dashboard data&hellip;</div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Dashboard</h1>
          <p className={styles.pageSubtitle}>System administration overview</p>
        </div>
        <button className={styles.btnSecondary} onClick={() => { setLoading(true); loadData(); }}>
          Refresh
        </button>
      </div>

      {error && (
        <div className={`${styles.statusBar} ${styles.status_error}`}>
          {error}
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div style={S.grid}>
        <div style={S.statCard} onClick={() => navigate('/admin/users')}>
          <div style={S.statValue}>{userCount}</div>
          <div style={S.statLabel}>Total Users</div>
          <div style={S.statSub}>{activeUsers} active &middot; {adminCount} admin{adminCount !== 1 ? 's' : ''}</div>
        </div>

        <div style={S.statCard} onClick={() => navigate('/admin/pillars')}>
          <div style={S.statValue}>{projectCount}</div>
          <div style={S.statLabel}>Projects</div>
          <div style={S.statSub}>{activeProjects} active</div>
        </div>

        <div style={S.statCard} onClick={() => navigate('/admin/geo')}>
          <div style={S.statValue}>{communityCount}</div>
          <div style={S.statLabel}>Communities</div>
          <div style={S.statSub}>{totalCommunities} total (incl. inactive)</div>
        </div>

        <div style={S.statCard} onClick={() => navigate('/admin/api')}>
          <div style={{ ...S.statValue, color: healthColor(health.overallStatus) }}>
            {healthyEndpoints}/{totalEndpoints}
          </div>
          <div style={S.statLabel}>API Health</div>
          <div style={S.statSub}>
            <span style={S.dot(healthColor(health.overallStatus))} />
            {' '}{healthLabel(health.overallStatus)}
          </div>
        </div>

        {waitlistTotal > 0 && (
          <div style={S.statCard} onClick={() => navigate('/admin/users')}>
            <div style={{ ...S.statValue, color: waitlistPending > 0 ? '#d97706' : '#16a34a' }}>
              {waitlistPending}
            </div>
            <div style={S.statLabel}>Waitlist Pending</div>
            <div style={S.statSub}>{waitlistTotal} total applications</div>
          </div>
        )}
      </div>

      {/* ── Middle Row: Audit Log + API Endpoints ── */}
      <div style={S.row}>
        {/* Recent Activity */}
        <div style={{ ...S.panel, flex: 2 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={S.sectionTitle}>Recent Activity</h2>
            <button
              className={styles.btnSecondary}
              style={{ padding: '4px 10px', fontSize: 11 }}
              onClick={() => navigate('/admin/audit')}
            >
              View All
            </button>
          </div>
          {data?.auditLog && data.auditLog.entries.length > 0 ? (
            <>
              {data.auditLog.entries.slice(0, 8).map((entry) => (
                <div key={entry.id} style={S.auditRow}>
                  <span style={S.badge(eventTypeColor(entry.eventType))}>
                    {formatEventType(entry.eventType)}
                  </span>
                  <span style={{ flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.details || entry.eventType}
                  </span>
                  <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>
                    {formatTimeAgo(entry.createdAt)}
                  </span>
                </div>
              ))}
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
                {data.auditLog.totalCount} total events
              </div>
            </>
          ) : (
            <div style={{ color: '#9ca3af', fontSize: 13, padding: '20px 0', textAlign: 'center' }}>
              {data?.auditLog ? 'No recent activity' : 'Audit log not available'}
            </div>
          )}
        </div>

        {/* API Endpoint Status */}
        <div style={S.panel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={S.sectionTitle}>Endpoint Status</h2>
            <button
              className={styles.btnSecondary}
              style={{ padding: '4px 10px', fontSize: 11 }}
              onClick={() => navigate('/admin/api')}
            >
              Details
            </button>
          </div>
          {health.endpoints.map((ep) => (
            <div key={ep.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13 }}>
              <span style={S.dot(
                ep.status === 'up' ? '#16a34a' : ep.status === 'degraded' ? '#d97706' : ep.status === 'down' ? '#dc2626' : '#9ca3af'
              )} />
              <span style={{ flex: 1, color: '#374151' }}>{ep.name}</span>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                {ep.status === 'up' || ep.status === 'degraded'
                  ? `${ep.responseMs}ms`
                  : ep.status === 'checking' ? '\u2026' : 'down'}
              </span>
            </div>
          ))}
          {health.lastPollAt > 0 && (
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
              Last checked: {new Date(health.lastPollAt).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* ── Quick Links ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={S.sectionTitle}>Quick Actions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
          {QUICK_LINKS.map((link) => (
            <button
              key={link.to}
              style={S.quickLink}
              onClick={() => navigate(link.to)}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#45BB47'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#e5e7eb'; }}
            >
              <span style={{ fontSize: 16, opacity: 0.6 }}>{link.icon}</span>
              {link.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Audit event formatting ─────────────────────────────────────

function formatEventType(raw: string): string {
  return raw
    .replace(/([A-Z])/g, ' $1')
    .replace(/[._-]/g, ' ')
    .trim()
    .split(' ')
    .slice(0, 3)
    .join(' ');
}

function eventTypeColor(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('login') || lower.includes('auth')) return '#2563eb';
  if (lower.includes('create') || lower.includes('add') || lower.includes('invite')) return '#16a34a';
  if (lower.includes('delete') || lower.includes('remove') || lower.includes('suspend')) return '#dc2626';
  if (lower.includes('update') || lower.includes('edit') || lower.includes('change')) return '#d97706';
  return '#6b7280';
}
